import { useEffect, useRef, useState } from 'react'
import { AlertTriangle, Check, RotateCcw, X } from 'lucide-react'
import { EYEBROW } from '../cc/tokens'
import { ago, dateTime } from '../lib/format'
import { EIM_STATUS_STYLE } from './statusStyle'
import {
  fetchEimOverheatEpisodesToday, fetchEimOverheatTrend,
  formatIdlePowerW, GTI_LOW_SOC_PCT, idleMinutes, overheatMinutes,
  type EimAlertAck, type EimAlertKind, type EimOverheatEpisode, type EimOverheatSample, type EimVehicle,
} from './useEimApi'

/** Matches an episode to this vehicle's current live `overheat_since` when it's set
 *  (the common case, still alerting) -- falling back to the most recently reported
 *  episode ONLY when `overheat_since` is null (SPEC.md AC-10: a cleared-but-same-day
 *  episode has no live `overheat_since` to match against at all).
 *
 *  Returns null, not a stale fallback, when `overheat_since` IS set but doesn't match
 *  any fetched episode (adversarial review, MED) -- the episode row for a just-started
 *  alert doesn't exist yet for its first ~5 minutes (f_eim_sync_overheat_episodes' own
 *  300s sustain gate), so falling back to "the latest episode" during that window
 *  showed a PREVIOUS, already-cleared episode's resolved-at/ambient/vehicle-state
 *  against a currently-firing alert -- confirmed reproducible: a card could read
 *  "triggered just now / resolved 90 minutes ago". Honest "no data yet" (the UI already
 *  renders '—' for a null episode) beats a confidently-wrong old one. */
function matchingOverheatEpisode(v: EimVehicle, episodes: EimOverheatEpisode[]): EimOverheatEpisode | null {
  if (v.overheat_since) {
    const since = new Date(v.overheat_since).getTime()
    return episodes.find((e) => new Date(e.episode_started_ts).getTime() === since) ?? null
  }
  return episodes[episodes.length - 1] ?? null
}

/** Ultratech's own AlertDetail.tsx pattern (TriagePill + Action buttons), ported to EIM's
 *  simpler two-state model -- there is no "resolved" here because an idle/low-battery
 *  alert isn't a persisted incident with a lifecycle of its own the way a breakdown is:
 *  it's a live read off telemetry (isIdleAlert/isLowSoc in useEimApi.ts) that clears itself
 *  the moment the vehicle moves or charges back up. Acknowledged is the only state worth
 *  recording, which is exactly what eim_alert_ack persists.
 *
 *  No "call driver" action, unlike Ultratech's own AlertDetail -- EIM's vendor feed carries
 *  no driver name or phone number anywhere (see EimVehicle in useEimApi.ts), so there is
 *  nothing honest to wire that button to here. */

const SITE_LABEL: Record<EimVehicle['site'], string> = { gti: 'GTI', jm_baxi: 'JM Baxi' }

export function EimAlertDetail({
  v, kind, ack, onClose, onAck, onUnack,
}: {
  v: EimVehicle
  kind: EimAlertKind
  ack: EimAlertAck | null
  onClose: () => void
  onAck: () => Promise<void>
  onUnack: () => Promise<void>
}): JSX.Element {
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const first = useRef<HTMLButtonElement>(null)

  // Real-time triggered-at/cleared-at/ambient lifecycle, BATTERY_OVERHEAT only -- see
  // SPEC.md section 2.5. idle/low-battery have no equivalent episode fetch (this file's
  // own header comment on why those two kinds stay ack-only).
  const [episode, setEpisode] = useState<EimOverheatEpisode | null>(null)
  const [trend, setTrend] = useState<EimOverheatSample[] | null>(null)
  const [trendLoading, setTrendLoading] = useState(false)

  useEffect(() => {
    if (kind !== 'BATTERY_OVERHEAT') return
    let cancelled = false
    fetchEimOverheatEpisodesToday(v.site, v.vehicle_id)
      .then((episodes) => { if (!cancelled) setEpisode(matchingOverheatEpisode(v, episodes)) })
      .catch(() => { if (!cancelled) setEpisode(null) })
    return () => { cancelled = true }
    // [kind, v.site, v.vehicle_id], NOT [kind, v] (adversarial review, MED) -- `v` comes
    // from vehicles.find(...) in EimShell.tsx's own live-poll state, a fresh object on
    // every 10s refresh() even when every field's VALUE is unchanged -- depending on the
    // whole object re-fetched this endpoint every 10s for as long as the modal stayed
    // open, violating SPEC.md's own "zero new recurring poll loops" non-goal. Confirmed
    // live: 3 renders with equal-valued, new-identity `v` objects produced 3 fetches
    // before this fix, 1 after.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [kind, v.site, v.vehicle_id])

  async function viewTrend(): Promise<void> {
    const episodeStartedTs = episode ? String(episode.episode_started_ts) : v.overheat_since
    if (!episodeStartedTs) return
    setTrendLoading(true)
    try {
      setTrend(await fetchEimOverheatTrend(v.site, v.vehicle_id, episodeStartedTs))
    } catch {
      setTrend([])
    } finally {
      setTrendLoading(false)
    }
  }

  useEffect(() => { first.current?.focus() }, [])
  useEffect(() => {
    const esc = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose() }
    window.addEventListener('keydown', esc)
    return () => window.removeEventListener('keydown', esc)
  }, [onClose])

  const style = kind === 'IDLE_ALERT' ? EIM_STATUS_STYLE.alert
    : kind === 'BATTERY_OVERHEAT' ? EIM_STATUS_STYLE.overheat
    : EIM_STATUS_STYLE.breakdown
  const label = kind === 'IDLE_ALERT' ? 'idle alert' : kind === 'BATTERY_OVERHEAT' ? 'battery overheat' : 'low battery'

  // overheatMinutes reads v.overheat_since, which is live telemetry state -- null the
  // moment the vehicle itself is no longer overheating. That's every RESOLVED alert
  // (the common case for anything opened from the panel, not just the instant it
  // fires), so without this fallback "sustained" rendered blank for every cleared
  // BATTERY_OVERHEAT card. episode.duration_sec is the same fact, persisted -- same
  // episode-fallback pattern `triggered`/`resolved`/`ambient`/`vehicle state` below
  // already use for exactly this reason.
  const liveSustainedMin = overheatMinutes(v, Date.now())
  const sustainedMin = liveSustainedMin ?? (episode ? Math.floor(episode.duration_sec / 60) : null)

  async function act(fn: () => Promise<void>): Promise<void> {
    setBusy(true)
    setError(null)
    try {
      await fn()
    } catch (e) {
      setError(e instanceof Error ? e.message : 'could not reach the server')
    } finally {
      setBusy(false)
    }
  }

  return (
    <div className="fixed inset-0 z-50 grid place-items-center bg-[rgba(15,23,42,0.28)] p-4"
         onMouseDown={(e) => { if (e.target === e.currentTarget) onClose() }}>
      <div role="dialog" aria-modal="true" aria-label={`${label} for ${v.vehicle_id}`}
           className={`w-full animate-slide-up rounded-xl border border-[rgba(15,23,42,0.10)]
                      bg-white shadow-[0_24px_60px_-24px_rgba(15,23,42,0.55)]
                      ${kind === 'BATTERY_OVERHEAT' ? 'max-w-[460px]' : 'max-w-[380px]'}`}>
        <header className="flex items-center gap-2.5 border-b border-[rgba(15,23,42,0.07)] px-4 py-3">
          <span className="grid h-7 w-7 shrink-0 place-items-center rounded-lg" style={{ backgroundColor: style.bg }}>
            <AlertTriangle size={15} style={{ color: style.text }} />
          </span>
          <div className="min-w-0">
            <div className="font-mono text-[13px] font-semibold text-[#0F172A]">{v.vehicle_id}</div>
            <div className={EYEBROW}>{SITE_LABEL[v.site]} · {label}</div>
          </div>
          <button onClick={onClose} aria-label="Close"
                  className="ml-auto rounded p-1 text-[#8794A7] hover:bg-[rgba(15,23,42,0.05)]">
            <X size={15} />
          </button>
        </header>

        <div className="flex flex-col gap-3 px-4 py-3.5">
          {kind === 'IDLE_ALERT' ? (
            <div>
              <div className={EYEBROW}>idling</div>
              <div className="mt-0.5 text-[22px] font-semibold tabular-nums text-[#0F172A]">
                {idleMinutes(v, Date.now())}m
              </div>
              {v.idle_power_w != null && (
                <div className="mt-0.5 text-[12px] tabular-nums text-[#64748B]">
                  {formatIdlePowerW(v.idle_power_w)} draw · {v.soc != null ? `${v.soc.toFixed(0)}% SOC` : '—'}
                </div>
              )}
            </div>
          ) : kind === 'BATTERY_OVERHEAT' ? (
            <div>
              <div className={EYEBROW}>max cell temperature</div>
              <div className="mt-0.5 text-[22px] font-semibold tabular-nums text-[#9A3412]">
                {v.max_cell_temp != null ? `${v.max_cell_temp.toFixed(1)}°C` : '—'}
              </div>
              <div className="mt-0.5 text-[12px] tabular-nums text-[#64748B]">
                sustained {sustainedMin != null ? `${sustainedMin}m` : '—'}
              </div>

              <div className="mt-2.5 grid grid-cols-2 gap-x-3 gap-y-1.5 text-[12px] text-[#64748B]">
                <div>
                  <div className={EYEBROW}>triggered</div>
                  <div className="tabular-nums text-[#0F172A]">
                    {v.overheat_since ? dateTime(new Date(v.overheat_since).getTime())
                      : episode ? dateTime(new Date(episode.episode_started_ts).getTime()) : '—'}
                  </div>
                </div>
                <div>
                  {/* "resolved", not "cleared" -- the alert-list row (EimShell.tsx) also
                      renders a literal "cleared" label and stays mounted underneath this
                      modal (it's an overlay, not a route change), so a second literal
                      "cleared" here would make every unscoped screen.queryByText(/cleared/i)
                      in EimAlertDetailOverheat.test.ts ambiguous (2 matches).

                      Gated on cleared_ts (open vs. closed), but the VALUE shown is
                      episode_ended_ts, not cleared_ts -- BUG fix: cleared_ts is this
                      episode's DETECTION time (f_eim_sync_overheat_episodes runs once per
                      site per poll and closes every no-longer-current episode for that
                      site in one statement, so same-poll closes legitimately share one
                      cleared_ts -- confirmed live: 5 vehicles with trigger times 5 hours
                      apart all showing the identical cleared time). episode_ended_ts is
                      the real fact this field is meant to answer -- this vehicle's own
                      last confirmed-overheated poll, refreshed every cycle by the SQL
                      function's `extended`/`closed` CTEs -- and genuinely differs per
                      vehicle even when cleared_ts doesn't. */}
                  <div className={EYEBROW}>resolved</div>
                  <div className="tabular-nums text-[#0F172A]">
                    {episode?.cleared_ts ? dateTime(new Date(episode.episode_ended_ts).getTime()) : '—'}
                  </div>
                </div>
                <div>
                  <div className={EYEBROW}>ambient</div>
                  <div className="tabular-nums text-[#0F172A]">
                    {episode?.ambient_temp_c != null ? `${episode.ambient_temp_c.toFixed(1)}°C` : '—'}
                  </div>
                </div>
                <div>
                  <div className={EYEBROW}>vehicle state</div>
                  <div className="text-[#0F172A]">{episode?.vehicle_status ?? v.status}</div>
                </div>
              </div>

              <button
                type="button"
                disabled={trendLoading}
                onClick={() => void viewTrend()}
                className="mt-2.5 text-[11px] font-semibold text-[#9A3412] underline-offset-2 hover:underline disabled:opacity-50"
              >
                {trendLoading ? 'Loading trend…' : 'View trend'}
              </button>

              {trend && (
                trend.length === 0 ? (
                  <div className="mt-1.5 text-[11px] text-[#64748B]">no per-poll samples captured for this episode</div>
                ) : (
                  <div className="mt-1.5 flex items-end gap-0.5" aria-label="temperature trend">
                    {(() => {
                      // Scaled off this episode's OWN peak, not a hard-coded 60C ceiling
                      // (adversarial review, LOW -- a real episode can exceed 60C, see
                      // EIM_BATTERY_OVERHEAT_THRESHOLD_C's own neighboring comments on
                      // readings up to 125C, which overflowed the fixed-scale bars).
                      const peak = Math.max(...trend.map((s) => s.max_cell_temp), 1)
                      return trend.map((s, i) => (
                        <div
                          key={i}
                          title={`${s.max_cell_temp.toFixed(1)}°C`}
                          className="w-1.5 rounded-t bg-[#C2410C]"
                          style={{ height: `${Math.max(4, (s.max_cell_temp / peak) * 36)}px` }}
                        />
                      ))
                    })()}
                  </div>
                )
              )}
            </div>
          ) : (
            <div>
              <div className={EYEBROW}>state of charge</div>
              <div className="mt-0.5 text-[22px] font-semibold tabular-nums text-[#9F1239]">
                {v.soc != null ? `${v.soc.toFixed(0)}%` : '—'}
              </div>
              <div className="mt-0.5 text-[12px] text-[#64748B]">below the {GTI_LOW_SOC_PCT}% GTI floor</div>
            </div>
          )}

          {ack ? (
            <div className="rounded-md bg-[rgba(245,158,11,0.10)] px-2.5 py-1.5 text-[11px] text-[#B45309]">
              acknowledged by {ack.by_whom} · {ago(ack.acked_ts)} ago
            </div>
          ) : (
            <div className="rounded-md bg-[rgba(15,23,42,0.04)] px-2.5 py-1.5 text-[11px] text-[#64748B]">
              not yet acknowledged
            </div>
          )}

          {error && <div className="text-[11px] text-[#9F1239]">{error}</div>}

          <div className="flex gap-2">
            {ack ? (
              <button
                ref={first}
                type="button"
                disabled={busy}
                onClick={() => void act(onUnack)}
                className="flex flex-1 items-center justify-center gap-1.5 rounded-lg border border-[rgba(15,23,42,0.10)]
                           px-3 py-2 text-[12px] font-semibold text-[#64748B] transition-colors
                           hover:border-[rgba(15,23,42,0.18)] hover:text-[#0F172A] disabled:opacity-50"
              >
                <RotateCcw size={13} /> {busy ? 'Reopening…' : 'Reopen'}
              </button>
            ) : (
              <button
                ref={first}
                type="button"
                disabled={busy}
                onClick={() => void act(onAck)}
                className="flex flex-1 items-center justify-center gap-1.5 rounded-lg border border-[rgba(16,185,129,0.32)]
                           bg-[rgba(16,185,129,0.10)] px-3 py-2 text-[12px] font-semibold text-[#047857]
                           transition-colors hover:bg-[rgba(16,185,129,0.18)] disabled:opacity-50"
              >
                <Check size={13} /> {busy ? 'Acknowledging…' : 'Acknowledge'}
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  )
}
